

import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import { Getter, namespace, State } from "vuex-class"

import { DataDefinition, DataSource } from "@/types"
import DataModal from "@/components/Config/DataModal.vue"
import StringValidation from "@/scripts/stringValidation"
import api from "@/services/api"
import { BModal } from "bootstrap-vue"
import SuggestList from "@/components/Fields/SuggestList.vue"
import { Folder, FolderType } from "@/types/folder"
import { TenantFeatures } from "@/types/tenantFeatures"
import { folderMixin } from "@/mixins/folderMixin"
import { IntegrationWithSettings } from "@/types/integration"
import integrationApi from "@/services/api_Integrations"

const setup = namespace("setup")

@Component({
	components: {
		"data-modal": DataModal
	}
})
export default class SourceEdit extends Vue {
	$refs!: {
		dataModal: BModal
	}

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	tenantFeatures = TenantFeatures

	@Prop({ default: () => null })
	value!: DataSource | null

	@setup.Getter("getTeams")
	public teams!: any[]

	@setup.Getter("getDefinitions")
	public definitions!: DataDefinition[]

	@setup.Getter("getDataSources")
	public dataSources!: DataSource[] | null

	@setup.Mutation
	public updateDataSource!: (dataSource : DataSource) => void

	@setup.Mutation
	public addDataSource!: (dataSource : DataSource) => void

	@setup.Mutation
	public removeDataSource!: (dataSource : DataSource) => void

	folderPaths: string[] = []
	externalDataSourceIntegration: IntegrationWithSettings = {} as IntegrationWithSettings

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => StringValidation.validateTitle(item.title)
			},
			{
				key: "folder",
				label: "Folder",
				sortable: true,
				dataType: "text",
				dynamicComponent: SuggestList,
				componentProperties: {
					placeholder: "Data Source Folder",
					selectOptions: this.folderPaths
				},
				inModal: () => this.dataSourceFoldersFeatureIsEnabled,
				description: ""
			},
			{
				key: "definitionTitle",
				label: "Item Type",
				sortable: true,
				dataType: "list",
				valueColumn: "definitionId",
				options: this.definitions,
				description: "Type of data in this list. Leave empty for a simple list."
			},
			{
				key: "includedLists",
				label: "Included Lists",
				dataType: "list-multiple",
				valueColumn: "includedLists",
				options: this.dataSources,
				description: "Include items from list in this one. It won't include any additional lists included to these",
				errors: (item: any) => this.checkForSelfDataSource(item)
			},
			{
				key: "editPermissions",
				label: "Edit Permissions",
				dataType: "list-multiple",
				valueColumn: "editPermissions",
				options: this.teams,
				description: "Which Roles are able to edit items in this data source"
			},
			{
				key: "createdBy.name",
				label: "Created By",
				inModal: () => false,
				hasTemplate: true
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				inModal: () => false,
				hasTemplate: true
			},
			{
				key: "buttons",
				label: "",
				inModal: () => false,
				hasTemplate: true
			}
		]
	}

	async mounted () {
		try {
			this.externalDataSourceIntegration = await integrationApi.integrationGetById("externalDataSourceSync")
		} catch {
			// Integration optional – safe to ignore error
		}
	}

	@Watch("value")
	async onValueChanged () {
		if (this.value) {
			if (this.dataSourceFoldersFeatureIsEnabled) {
				await this.fetchFolders()
			}

			this.$refs.dataModal.open(this.value)
		}
	}

	private async fetchFolders (): Promise<void> {
		let folders = await api.foldersGet(FolderType.DataSource)
		this.folderPaths = folderMixin.methods.getListOfFolderPathsToRootFolder(folders, true)
	}

	private get dataSourceFoldersFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.DataSourceFolders)
	}

	private checkForSelfDataSource (item: any): string | null {
		if (item.includedLists && item.includedLists.some((i: any) => i === item.id)) {
			return `Data source cannot be added to itself: ${item.title}`
		}

		return null
	}

	private get isMappedInExternalIntegration (): boolean {
		if (!this.value?.id || !this.externalDataSourceIntegration?.settings?.filePathMappings) {
			return false
		}

		return this.externalDataSourceIntegration.settings.filePathMappings.some(
			(mapping: any) => mapping.id === this.value!.id
		)
	}

	async save (source: DataSource) {
		if (this.dataSources) {
			source.includedLists = source.includedLists
				.map((listId: any) => listId.id
					? listId
					: this.dataSources!.find((included: any) => included.id === listId))

			const id = await api.dataSourceSave(source)

			if (source.id) {
				this.updateDataSource(source)
				this.$emit("updated", source)
			} else if (id) {
				const createdSource = {
					...source,
					id
				}

				this.addDataSource(createdSource)
				this.$emit("created", createdSource)
			}
		}
	}

	async deleteSource (dataSource: any) {
		await api.dataSourceDelete(dataSource)
		this.removeDataSource(dataSource)

		this.$emit("deleted", dataSource)
	}

	async dataSourceDeleteDetails (dataSource: any) {
		if (!dataSource!.id) {
			return
		}

		return api.dataSourceUsage(dataSource)
	}
}

